///
/// EarthBrowser
///
/// Created by Matt Giger on 10/22/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"

/**
 * One 10-minute frame of the global ~2 km cloud mosaic (the "mosaic" shard
 * of the clouds dataset; see weather-services cloud-mosaic.ts): a tile
 * pyramid rooted at base_url, <level>/<row>/<col>.webp, level 0 being
 * 1024x512 and each level doubling.
 */
export interface CloudMosaicFrameProperties {
	time: string
	base_url: string
	width: number
	height: number
	tile: number
	levels: number
}

export class CloudMosaicTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists cloud_mosaic_frames (
					time text primary key,
					base_url text not null,
					width integer not null,
					height integer not null,
					tile integer not null,
					levels integer not null
				)`
			},
		])
	}

	public static async replaceAll(rows: CloudMosaicFrameProperties[]): Promise<void> {
		await Database.db.exec("delete from cloud_mosaic_frames", [], "critical")
		if (rows.length === 0) return
		await Database.db.bulkInsert(
			`insert into cloud_mosaic_frames (time, base_url, width, height, tile, levels)
			 values (?, ?, ?, ?, ?, ?)
			 on conflict(time) do update set
				base_url = excluded.base_url, width = excluded.width, height = excluded.height,
				tile = excluded.tile, levels = excluded.levels`,
			rows.map(r => [r.time, r.base_url, r.width, r.height, r.tile, r.levels]))
	}

	/** Frames newer than the cutoff, oldest first */
	public static async getSince(cutoffIso: string): Promise<CloudMosaicFrameProperties[]> {
		const rows = await Database.db.exec(
			"select * from cloud_mosaic_frames where time >= ? order by time asc",
			[cutoffIso],
			"low"
		)
		return rows as CloudMosaicFrameProperties[]
	}
}
